import ProductDetails from '@interfaces/ProductDetails.interface';
import BaseApiClient from '../BaseApiClient';
import { DocumentInterface } from '@interfaces/Document.interface';
import {
  FilterListProductForm,
  ListProductForm
} from '@interfaces/ListProductForm.interface';
import { ProductAttributes } from '@interfaces/ProductAttributes.interface';
import axios from 'axios';
import { SavedSearch } from '@interfaces/SavedSearches.interface';
import { InsuranceProduct } from '@interfaces/ProductsInsuranceAndWealth';
import { ListDataParams, ListDataResponse } from '../types';
import { linesOfBusinessFromApiResponse } from '@/utils/linesOfBusinessFromApiResponse';
import { marketplaceLinesOfBusinessQueryString } from '@/utils/marketplaceLinesOfBusinessQuery';

axios.defaults.withCredentials = true;
interface MetaData {
  current_page: number;
  pages: number;
  per_page: number;
  total: number;
}

interface SearchResultItem {
  // eslint-disable-next-line
  [key: string]: any;
}
interface GetPageParams {
  page?: number;
  per_page?: number;
  sort?: string | string[];
  filter?: SearchResultItem;
}

export class ApiProducts extends BaseApiClient {
  async all(): Promise<Array<ProductDetails[]>> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/products', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getProductTypes(): Promise<Array<{ name: string; type: string }>> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/product-types', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getInsuranceAndWealthProducts(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<InsuranceProduct>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/products', listData, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then(({ data }) => {
          if (data) {
            resolve({
              items: data.data,
              pagination: {
                page: data.meta?.page,
                perPage: data.meta?.per_page,
                total: data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getMyProducts({
    page,
    per_page = 12
  }: GetPageParams): Promise<{ data: InsuranceProduct[]; meta: MetaData }> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/products/my-products?page[number]=${page}&page[size]=${per_page}`,
          {
            headers: {
              Version: '2022-12-23'
            }
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  async getInsuranceAndWealthProductDetails(
    id: string
  ): Promise<InsuranceProduct> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/products/marketplace/${id}`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  // eslint-disable-next-line
  async getBookMarks(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        // TODO : use the correct API
        .get('/products?bookmarked=true', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async createProductListing(
    fields: ListProductForm,
    published: string,
    requiredMultpart: boolean
  ): Promise<ProductDetails> {
    const multipartHeader = requiredMultpart
      ? {
          headers: {
            'Content-Type': 'multipart/form-data',
            Version: '2022-12-23'
          }
        }
      : {};

    const filteredFields = Object.fromEntries(
      Object.entries(fields).filter(([_, value]) => value !== '')
    );

    return new Promise((resolve, reject) => {
      this.client
        .post(
          '/products',
          { ...filteredFields, published },
          { ...multipartHeader }
        )
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getProduct(id: string): Promise<ProductDetails> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/products/${id}`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getProductData(): Promise<ProductAttributes> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/products/data', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getDocuments(id: string): Promise<DocumentInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(`/documents/${id}`, undefined, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async productSellRequest(uuid?: string): Promise<{ statusCode: number }> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/products/${uuid}/request-to-sell`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.status === 204) {
            resolve({ statusCode: res.status });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async setBookmarkItem(id: string) {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/products/${id}/bookmark`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.status === 204) {
            resolve(res.status);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async delist(uuid: string): Promise<ProductDetails> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`products/${uuid}/publish`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async storeDocument(
    productId: number | string,
    files: Array<File>
  ): Promise<DocumentInterface[]> {
    return new Promise((resolve) => {
      const fd = new FormData();

      files.map((file) => {
        return fd.append('files[]', file, file.name);
      });

      this.client
        .post(`/products/${productId}/documents`, fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  async deleteProductDocuments(productId: number, ids: number[]) {
    return new Promise((resolve) => {
      this.client
        .delete(
          `/products/${productId}/documents?document_ids=${ids.slice(
            0,
            ids.length - 1
          )}`,
          {
            headers: {
              Version: '2022-12-23'
            }
          }
        )
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  async saveProductFilterSearch(
    searchQuery: FilterListProductForm
  ): Promise<{ data: SavedSearch }> {
    return new Promise((resolve, reject) => {
      this.client
        .post(
          '/products/saved-search',
          {
            filter: searchQuery
          },
          {
            headers: {
              Version: '2022-12-23'
            }
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getProductFilterSearches(): Promise<SavedSearch[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/products/saved-search', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async deleteProductFilterSearch(filterId: string) {
    return new Promise((resolve, reject) => {
      this.client
        .delete('/products/saved-search', {
          data: { id: filterId },
          headers: { Version: '2022-12-23' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async deleteAllProductFilterSearches() {
    return new Promise((resolve, reject) => {
      this.client
        .delete('/products/saved-search', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getInsuranceProducts(searchTerm?: string): Promise<ProductDetails[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/products/insurance?page[size]=20${
            searchTerm ? `&filter[name]=${searchTerm}` : ''
          }`,
          {
            headers: {
              Version: '2022-12-23'
            }
          }
        )
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getInsuranceProduct(productUUID: string): Promise<ProductDetails> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/products/${productUUID}`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async getBookmarkedInsuranceProducts(): Promise<ProductDetails[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/products/insurance?filter[is_bookmarked]=true', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          resolve(res.data.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  assembleFormData(fields: Record<string, any>): FormData {
    const formData = new FormData();

    const appendFormData = (data: any, parentKey?: string) => {
      if (data === null || data === undefined) {
        formData.append(parentKey!, ''); // Null/undefined become empty string
      } else if (
        typeof data === 'string' ||
        typeof data === 'number' ||
        data instanceof Blob
      ) {
        formData.append(parentKey!, data as any);
      } else if (Array.isArray(data)) {
        data.forEach((item, index) => {
          const arrayKey = `${parentKey}[${index}]`;
          appendFormData(item, arrayKey);
        });
      } else if (typeof data === 'object') {
        Object.entries(data).forEach(([key, value]) => {
          const newKey = parentKey ? `${parentKey}[${key}]` : key;
          appendFormData(value, newKey);
        });
      }
    };

    try {
      Object.entries(fields).forEach(([key, value]) => {
        appendFormData(value, key);
      });
    } catch (err) {
      console.error(err);
    }

    // Append files if they exist
    if (fields.image_document)
      formData.append('image_document', fields.image_document);
    if (fields.video_document)
      formData.append('video_document', fields.video_document);
    if (fields.audio_document)
      formData.append('audio_document', fields.audio_document);
    if (fields.audio_image_document)
      formData.append('audio_image_document', fields.audio_image_document);

    return formData;
  }

  async createInsuranceProduct(
    fields: ListProductForm
  ): Promise<ProductDetails> {
    const formData = this.assembleFormData(fields);

    return new Promise((resolve, reject) => {
      this.client
        .post('/products/insurance', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => reject(err));
    });
  }

  async updateInsuranceProduct(
    fields: ListProductForm,
    id: string
  ): Promise<ProductDetails> {
    const formData = this.assembleFormData(fields);

    formData.append('_method', 'PATCH');

    return new Promise((resolve, reject) => {
      this.client
        .post(`/products/insurance/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async publishProduct(productUUID: string): Promise<ProductDetails> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/products/${productUUID}/publish`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async unpublishProduct(productUUID: string): Promise<InsuranceProduct> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/products/${productUUID}/publish`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async deleteProductDocument(
    productUUID: string,
    documentID: number
  ): Promise<ProductDetails> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/products/${productUUID}/documents/${documentID}`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async deleteProduct(productUUID: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/products/${productUUID}`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  async getClassesOfBusiness(): Promise<
    { name: string; id: number; count: number }[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/classes-of-business', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getLineTypes(): Promise<{ name: string; id: number }[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/line-types', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Lines of business for product hierarchy, filtered by class and line type (CRM-268).
   * Backend: GET /lines-of-business?filter[class_of_business_id]=&filter[line_type_id]=&page[size]=
   */
  async getLinesOfBusinessByHierarchy(params: {
    classOfBusinessId: number;
    lineTypeId: number;
  }): Promise<{ name: string; id: number }[]> {
    const { classOfBusinessId, lineTypeId } = params;
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/lines-of-business?page[size]=999&filter[class_of_business_id]=${classOfBusinessId}&filter[line_type_id]=${lineTypeId}`,
          {
            headers: {
              Version: '2022-12-23'
            }
          }
        )
        .then((res) => {
          resolve(linesOfBusinessFromApiResponse(res.data));
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Lines of business that have at least one published marketplace product,
   * scoped by class + line type (CRM-267).
   */
  async getMarketplaceLinesOfBusiness(params: {
    classOfBusinessId: number;
    lineTypeId: number;
  }): Promise<{ name: string; id: number }[]> {
    const { classOfBusinessId, lineTypeId } = params;
    const qs = marketplaceLinesOfBusinessQueryString(
      classOfBusinessId,
      lineTypeId
    );
    return new Promise((resolve, reject) => {
      this.client
        .get(`/lines-of-business?${qs}`, {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => {
          resolve(linesOfBusinessFromApiResponse(res.data));
        })
        .catch((err) => reject(err));
    });
  }

  async getProductHostingTypes(): Promise<
    { name: string; id: number; count: number }[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/product-hosting-types', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getMarketQuadrants(): Promise<
    {
      id: number;
      name: string;
      description: string;
      display: string;
    }[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/market-quadrants', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getManagingGeneralAgents(
    searchTerm?: string
  ): Promise<{ name: string; id: number }[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/managing-general-agents?page[size]=20${
            searchTerm ? `&filter[name]=${searchTerm}` : ''
          }`,
          {
            headers: {
              Version: '2022-12-23'
            }
          }
        )
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getInsurers(
    searchTerm?: string
  ): Promise<{ name: string; id: number }[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/insurers?page[size]=20${
            searchTerm ? `&filter[name]=${searchTerm}` : ''
          }`,
          {
            headers: {
              Version: '2022-12-23'
            }
          }
        )
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getIPTRates(): Promise<{ name: string; id: number }[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/ipt-rates', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getPremiumTypes(): Promise<{ name: string; id: number }[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/premium-types', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getContractTerms(): Promise<{ name: string; id: number }[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/contract-terms', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getChannels(): Promise<{ name: string; id: number }[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/channels', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getIcons(): Promise<
    {
      id: number;
      value: string;
      name: string;
    }[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/icons', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async getCostsAndFees(): Promise<
    {
      id: number;
      label: string;
      is_other: boolean;
      data_values: {
        id: number;
        other_value: string;
      }[];
      icon: null;
    }[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/data-lists?filter[name]=costs_and_fees', {
          headers: {
            Version: '2022-12-23'
          }
        })
        .then((res) => resolve(res.data.data[0].options))
        .catch((err) => reject(err));
    });
  }
}

export { productsQueryKeys } from './query-keys';
export type {
  ProductsInsuranceAndWealthListParams,
  ProductsMyProductsParams
} from './query-keys';
export {
  useProductsAllQuery,
  useProductsBookmarkedInsuranceProductsQuery,
  useProductsBookmarksQuery,
  useProductsChannelsQuery,
  useProductsClassesOfBusinessQuery,
  useProductsContractTermsQuery,
  useProductsCostsAndFeesQuery,
  useProductsDocumentsQuery,
  useProductsIconsQuery,
  useProductsInsuranceAndWealthListQuery,
  useProductsInsuranceAndWealthProductDetailsQuery,
  useProductsInsuranceProductQuery,
  useProductsInsuranceProductsQuery,
  useProductsInsurersQuery,
  useProductsIptRatesQuery,
  useProductsLineTypesQuery,
  useProductsManagingGeneralAgentsQuery,
  useProductsMarketQuadrantsQuery,
  useProductsMyProductsQuery,
  useProductsHostingTypesQuery,
  useProductsPremiumTypesQuery,
  useProductsProductDataQuery,
  useProductsProductQuery,
  useProductsProductTypesQuery,
  useProductsSavedSearchesQuery
} from './use-products-queries';
export {
  useProductsCreateInsuranceMutation,
  useProductsCreateListingMutation,
  useProductsDeleteAllFilterSearchesMutation,
  useProductsDeleteDocumentMutation,
  useProductsDeleteDocumentsMutation,
  useProductsDeleteFilterSearchMutation,
  useProductsDeleteProductMutation,
  useProductsDelistMutation,
  useProductsPublishMutation,
  useProductsSaveFilterSearchMutation,
  useProductsSellRequestMutation,
  useProductsSetBookmarkMutation,
  useProductsStoreDocumentMutation,
  useProductsUnpublishMutation,
  useProductsUpdateInsuranceMutation
} from './use-products-mutations';
export type {
  ProductsCreateListingVariables,
  ProductsDeleteDocumentVariables,
  ProductsDeleteDocumentsVariables,
  ProductsStoreDocumentVariables,
  ProductsUpdateInsuranceVariables
} from './use-products-mutations';
